import React, { useState, useEffect } from 'react';
import { GameStat, GamePeriodStat } from '../types';
import { apiService } from '../api';
import LoadingSpinner from './LoadingSpinner';

type SortField = 'gameName' | 'hourlyVolumeUSD' | 'hourlyGGRUSD' | 'dailyVolumeUSD' | 'dailyGGRUSD' | 'volumeUSD' | 'ggrUSD';
type SortDirection = 'asc' | 'desc';

interface GameStatsPageProps {
  onBack: () => void;
  onGameClick?: (gameId: number, gameName: string) => void;
}

const GameStatsPage: React.FC<GameStatsPageProps> = ({ onBack, onGameClick }) => {
  const [stats, setStats] = useState<GameStat[]>([]);
  const [periodStats, setPeriodStats] = useState<GamePeriodStat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('gameName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Get user's timezone offset (in hours, from -12 to +12)
  const getUserTimezoneOffset = (): number => {
    const offsetMinutes = new Date().getTimezoneOffset();
    // getTimezoneOffset returns offset in minutes, negative for positive timezones
    // Convert to hours and invert sign
    const offsetHours = -offsetMinutes / 60;
    // Clamp to valid range [-12, 12]
    return Math.max(-12, Math.min(12, Math.round(offsetHours)));
  };

  const [timezoneOffset, setTimezoneOffset] = useState<number>(getUserTimezoneOffset());
  const [isPeriodMode, setIsPeriodMode] = useState<boolean>(false);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');
      setIsPeriodMode(false);
      const response = await apiService.getGameStats();
      if (response.success) {
        setStats(response.stats);
        setPeriodStats([]);
      } else {
        setError(response.error || 'Failed to load game statistics');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load game statistics');
      console.error('Game stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodStats = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today

    if (end > now) {
      setError('End date cannot be in the future');
      return;
    }

    if (start > end) {
      setError('Start date must be before end date');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setIsPeriodMode(true);
      
      // Create date in UTC from the date string (YYYY-MM-DD)
      // The date string is interpreted as midnight UTC, then we apply the timezone offset
      const startUTC = new Date(startDate + 'T00:00:00Z');
      // Apply timezone offset: if user selected UTC+3, we need to subtract 3 hours from UTC
      // to get the start of day in that timezone (in UTC representation)
      const startTimestamp = startUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
      
      // Create end date in UTC
      const endUTC = new Date(endDate + 'T23:59:59.999Z');
      // Apply timezone offset
      const endTimestamp = endUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
      
      const response = await apiService.getGamePeriodStats({
        startDate: startTimestamp,
        endDate: endTimestamp,
      });
      
      if (response.success) {
        setPeriodStats(response.stats);
        setStats([]);
      } else {
        setError(response.error || 'Failed to load period statistics');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load period statistics');
      console.error('Period stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMaxDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const isDatesValid = (): boolean => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return start <= end && end <= now;
  };

  // Generate timezone options from UTC-12 to UTC+12
  const timezoneOptions = Array.from({ length: 25 }, (_, i) => {
    const offset = i - 12; // From -12 to +12
    const sign = offset >= 0 ? '+' : '';
    return { value: offset, label: `UTC${sign}${offset}` };
  });

  useEffect(() => {
    loadStats();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedStats = [...stats].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    switch (sortField) {
      case 'gameName':
        aValue = a.gameName;
        bValue = b.gameName;
        break;
      case 'hourlyVolumeUSD':
        aValue = a.hourlyVolumeUSD;
        bValue = b.hourlyVolumeUSD;
        break;
      case 'hourlyGGRUSD':
        aValue = a.hourlyGGRUSD;
        bValue = b.hourlyGGRUSD;
        break;
      case 'dailyVolumeUSD':
        aValue = a.dailyVolumeUSD;
        bValue = b.dailyVolumeUSD;
        break;
      case 'dailyGGRUSD':
        aValue = a.dailyGGRUSD;
        bValue = b.dailyGGRUSD;
        break;
      default:
        return 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    }
  });

  // Sort period stats
  const sortedPeriodStats = [...periodStats].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    switch (sortField) {
      case 'gameName':
        aValue = a.gameName;
        bValue = b.gameName;
        break;
      case 'volumeUSD':
        aValue = a.volumeUSD;
        bValue = b.volumeUSD;
        break;
      case 'ggrUSD':
        aValue = a.ggrUSD;
        bValue = b.ggrUSD;
        break;
      default:
        return 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    }
  });

  // Filter stats by search term (case-insensitive, substring match)
  const filteredStats = searchTerm.trim() === ''
    ? sortedStats
    : sortedStats.filter(stat =>
        stat.gameName.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const filteredPeriodStats = searchTerm.trim() === ''
    ? sortedPeriodStats
    : sortedPeriodStats.filter(stat =>
        stat.gameName.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return '↕️';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="game-stats-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Game Statistics</h1>
        <button onClick={onBack} className="btn btn-logout" style={{ padding: '8px 16px' }}>
          ← Back to Games
        </button>
      </div>

      {/* Date range selector */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: '#f9f9f9', 
        borderRadius: '8px',
        border: '1px solid #ddd'
      }}>
        <div style={{ marginBottom: '10px', fontWeight: '500', color: '#333' }}>
          Select Date Range
        </div>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={getMaxDate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={getMaxDate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
            <select
              value={timezoneOffset}
              onChange={(e) => setTimezoneOffset(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {timezoneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={loadPeriodStats}
            disabled={!isDatesValid() || loading}
            style={{
              padding: '10px 24px',
              backgroundColor: isDatesValid() && !loading ? '#4CAF50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isDatesValid() && !loading ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              marginTop: '20px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (isDatesValid() && !loading) {
                e.currentTarget.style.backgroundColor = '#45a049';
              }
            }}
            onMouseLeave={(e) => {
              if (isDatesValid() && !loading) {
                e.currentTarget.style.backgroundColor = '#4CAF50';
              }
            }}
          >
            OK
          </button>
          {isPeriodMode && (
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setIsPeriodMode(false);
                setPeriodStats([]);
                loadStats();
              }}
              style={{
                padding: '10px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '20px'
              }}
            >
              Reset to Current Stats
            </button>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search game by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            maxWidth: '400px'
          }}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            style={{
              padding: '10px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ✕ Clear
          </button>
        )}
        {searchTerm && (
          <span style={{ color: '#000', fontSize: '14px', fontWeight: '500' }}>
            Found: {isPeriodMode ? filteredPeriodStats.length : filteredStats.length} of {isPeriodMode ? periodStats.length : stats.length}
          </span>
        )}
      </div>

      {error && (
        <div className="error-message" style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          color: '#c33', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end' }}>
        <button 
          onClick={() => {
            if (isPeriodMode && startDate && endDate) {
              loadPeriodStats();
            } else {
              loadStats();
            }
          }} 
          className="btn btn-refresh" 
          disabled={loading || (isPeriodMode && !isDatesValid())}
          style={{ 
            padding: '10px',
            width: '40px',
            height: '40px',
            backgroundColor: '#4CAF50', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            boxShadow: loading ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#45a049';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#4CAF50';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }
          }}
          title="Reload statistics"
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{
              animation: loading ? 'spin 1s linear infinite' : 'none',
              transformOrigin: 'center'
            }}
          >
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
        </button>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <h2 style={{ margin: 0, color: '#333', fontSize: '18px', fontWeight: '600' }}>
          Overall Statistics (USD)
        </h2>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th 
                style={{ 
                  padding: '12px', 
                  textAlign: 'left', 
                  cursor: 'pointer',
                  userSelect: 'none',
                  borderBottom: '2px solid #ddd'
                }}
                onClick={() => handleSort('gameName')}
              >
                Game Name {getSortIcon('gameName')}
              </th>
              {isPeriodMode ? (
                <>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid #ddd'
                    }}
                    onClick={() => handleSort('volumeUSD')}
                  >
                    Volume {getSortIcon('volumeUSD')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid #ddd'
                    }}
                    onClick={() => handleSort('ggrUSD')}
                  >
                    GGR {getSortIcon('ggrUSD')}
                  </th>
                </>
              ) : (
                <>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid #ddd'
                    }}
                    onClick={() => handleSort('hourlyVolumeUSD')}
                  >
                    Hourly Volume {getSortIcon('hourlyVolumeUSD')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid #ddd'
                    }}
                    onClick={() => handleSort('hourlyGGRUSD')}
                  >
                    Hourly GGR {getSortIcon('hourlyGGRUSD')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid #ddd'
                    }}
                    onClick={() => handleSort('dailyVolumeUSD')}
                  >
                    Daily Volume {getSortIcon('dailyVolumeUSD')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid #ddd'
                    }}
                    onClick={() => handleSort('dailyGGRUSD')}
                  >
                    Daily GGR {getSortIcon('dailyGGRUSD')}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {isPeriodMode ? (
              filteredPeriodStats.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    {searchTerm ? `No games found matching "${searchTerm}"` : 'No statistics available for selected period'}
                  </td>
                </tr>
              ) : (
                filteredPeriodStats.map((stat) => (
                  <tr key={stat.gameId} style={{ borderBottom: '1px solid #eee' }}>
                    <td 
                      style={{ 
                        padding: '12px', 
                        fontWeight: '500',
                        cursor: onGameClick ? 'pointer' : 'default',
                        color: onGameClick ? '#4CAF50' : 'inherit',
                        textDecoration: onGameClick ? 'underline' : 'none'
                      }}
                      onClick={() => onGameClick && onGameClick(stat.gameId, stat.gameName)}
                      onMouseEnter={(e) => {
                        if (onGameClick) {
                          e.currentTarget.style.color = '#45a049';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onGameClick) {
                          e.currentTarget.style.color = '#4CAF50';
                        }
                      }}
                    >
                      {stat.gameName}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: stat.volumeUSD < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(stat.volumeUSD)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: stat.ggrUSD < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(stat.ggrUSD)}</td>
                  </tr>
                ))
              )
            ) : (
              filteredStats.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    {searchTerm ? `No games found matching "${searchTerm}"` : 'No statistics available'}
                  </td>
                </tr>
              ) : (
                filteredStats.map((stat) => (
                  <tr key={stat.gameId} style={{ borderBottom: '1px solid #eee' }}>
                    <td 
                      style={{ 
                        padding: '12px', 
                        fontWeight: '500',
                        cursor: onGameClick ? 'pointer' : 'default',
                        color: onGameClick ? '#4CAF50' : 'inherit',
                        textDecoration: onGameClick ? 'underline' : 'none'
                      }}
                      onClick={() => onGameClick && onGameClick(stat.gameId, stat.gameName)}
                      onMouseEnter={(e) => {
                        if (onGameClick) {
                          e.currentTarget.style.color = '#45a049';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onGameClick) {
                          e.currentTarget.style.color = '#4CAF50';
                        }
                      }}
                    >
                      {stat.gameName}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: stat.hourlyVolumeUSD < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(stat.hourlyVolumeUSD)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: stat.hourlyGGRUSD < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(stat.hourlyGGRUSD)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: stat.dailyVolumeUSD < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(stat.dailyVolumeUSD)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: stat.dailyGGRUSD < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(stat.dailyGGRUSD)}</td>
                  </tr>
                ))
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GameStatsPage;
